
import { useState, useEffect, useRef, useCallback, Suspense, lazy, type CSSProperties } from "react";
import { tapHaptic } from "./utils/haptics";
import { useAuthStore, selectUser, selectProfile, selectLoading, selectInitAuth } from "./store/authStore";
import {
  useNavigationStore,
  selectActivePage,
  selectProfileRouteUsername,
  selectModalContent,
  selectModalData,
  selectSetActivePage,
  selectSetProfileRouteUsername,
  selectSetModalContent,
  selectSetModalData,
} from "./store/navigationStore";
import { db, EYE_MODE } from "./firebase";
import { kapiOlcumBypassAcikMi } from "./app/kapiOlcumBypass";
import { hataMesaji } from "./utils/hataAlani";
import type { ModalDonusDurumu, ModalIcerigi, ModalVerisi } from "./app/appRouting";
import { profilKapisiGerekliMi, kesintiSeridiGerekliMi } from "./app/profilKapisi";
import ProfilKesintiSeridi from "./components/ProfilKesintiSeridi";
import { doc, setDoc, updateDoc, serverTimestamp as fsServerTimestamp } from "firebase/firestore";
import { initPushNotifications } from "./services/pushNotifications";
// P4-4 — Yeni kullanici onboarding wizard'i (interests + follows + push).
import useWelcomeWizard from "./hooks/useWelcomeWizard";
import WelcomeWizard from "./components/WelcomeWizard";
// P0-5 — KVKK açık rıza kapısı. Auth hazır olunca consents/{version}
// doc'u kontrol edilir; yoksa ConsentSheet (kapatılamaz) açılır.
import useConsentGate from "./hooks/useConsentGate";
import ConsentSheet from "./components/ConsentSheet";
import { navigateToNotification } from "./services/notifications";
import useIsMobile from "./utils/useIsMobile";
// P3-1 — Hesap Merkezi: bu cihazda giriş yapılan hesapların hafif kaydı
// (yalnız uid/username/avatar — şifre/token saklanmaz).
import { saveAccount } from "./services/accountSwitcher";

import Auth from "./Auth";
// EMİR #94.3 — yanlış-yokluk paketi: profil okunamadığında gösterilen durum.
// Statik import (lazy DEĞİL): tam da ağın çalışmadığı anda gösterilecek bir
// ekranın chunk'ını o anda indirmeye çalışmak, hatayı hatayla karşılamaktır.
import ProfilUlasilamiyor from "./components/ProfilUlasilamiyor";
// E-posta doğrulama linki markalı sayfada karşılanır (/auth/action).
import AuthActionHandler from "./AuthActionHandler";
import LegalPage from "./pages/legal/LegalPage";
import FirstSparkPushPrompt from "./components/FirstSparkPushPrompt";
// PR-1 — costMode bilgi çubuğu (sadece emergency modunda görünür).
import CostModeBanner from "./components/CostModeBanner";
import AppErrorBoundary from "./components/AppErrorBoundary";
import LogoBar from "./LogoBar";
import BottomNav from "./BottomNav";
import SideNav from "./SideNav";
import Hikayeler from "./Hikayeler";
// Faz 2 Madde 9 — Arka plan yükleme floating toast. App-level mount;
// NewClipDesktop modalı kapansa bile aktif upload görünür.
// FAZ 6 GELİŞTİRME 2 — KVKK cookie consent banner. localStorage'da bir
// kez karar verildikten sonra banner kaybolur (null döner). Component
// kendi state'ini yönetir; App.js sadece mount eder.
import CookieConsentBanner from "./myclipsv3/ui/CookieConsentBanner";

import "./App.css";
import "./premium.css"; /* Premium CSS - Cyan-Pink, Glassmorphism */
import "./pageTransitions.css"; /* FAZ 4D — sayfa geçiş animasyonları */
import "./myclipsv3/services/featureFlags"; // V3 dev helpers eager-load (window.mlv3SetMode)
import { renderPageContent as renderPage } from "./app/appPages";
import {
  useDeepLinkHandler,
  usePopstateHandler,
  useDiscoveryPopstateSync,
  isAnyPermalinkPath,
  computeClipModalPush,
} from "./app/appRouting";
import { withViewTransition } from "./app/viewTransition";
// P0-1 — akış/keşfet klip dokunuşunun hedef kararı (SAF, birim testli).
import { akisKlibiAcmaKarari } from "./app/klipAcmaKarari";
import { v3KlibineDevret } from "./app/klipV3Devri";
// Bildirim yolunun TEK yüklemi (regex kopyası YOK — bkz. gorunumFiltreUrl).
import { bildirimYolundaMi } from "./app/gorunumFiltreUrl";
// Çözücüden ÖNCE karşılanan üç yol (auth-action · kart şablonu · yasal
// sayfalar) — desenleri TEK KAYNAK'ta; ölü rota kapısı da oradan okur.
import {
  authActionYoluMu,
  kartSablonKoduCoz,
  yasalSlugCoz,
} from "./app/cozucuOncesiRotalar";
// Composer/kamera + check-in URL'i (tek kaynak: src/app/olusturUrl.ts).
import { olusturUrlYaz, olusturModaliMi } from "./app/olusturUrl";
import type { MekanYuku, OlusturModali } from "./app/olusturUrl";
import { renderModal as renderModalContent } from "./app/appModals";

// ── #1019 YAVAŞ AĞ — kart şablonu sayfası `lazy` ─────────────────────────
// ÖLÇÜLDÜ (main.*.js.map): bu zincirdeki `components/Labubu/CardArt.tsx` tek
// başına 15 609 B ile ANA PAKETTE duruyordu. Sayfa YALNIZ `/card/:code` ve
// `/?card=` yollarında render ediliyor (aşağıdaki iki erken-return); yani her
// açılışta indirilen ama neredeyse hiç kullanılmayan bir yük.
// ⚠️ `LegalPage` BİLEREK `lazy` YAPILMADI: `32-legal-privacy` yavaş-ağ
// ekseninin KONTROL GRUBUDUR (uzak veriye hiç bakmayan salt-metin sayfa,
// `tur.sh` AG_ONLY yorumu). Ona bir chunk isteği eklemek, "gecikme paketten mi
// içerikten mi" sorusunu ölçen tek dayanağı kirletirdi — kazanç (12 kB) o
// ölçüm yeteneğinden ucuzdur.
const CardTemplatePage = lazy(() => import("./components/Labubu/CardTemplatePage"));

// Lazy bileşenler → src/app/appPages.js ve src/app/appModals.js içinde



// P0: Build hash for stale-client detection — build-time sabit değer (runtime Date.now() kullanılmaz)
// REACT_APP_BUILD_SHA ortam değişkeni CI/CD'de git SHA ile set edilmeli:
//   REACT_APP_BUILD_SHA=$(git rev-parse --short HEAD)
// Yoksa package.json version + env fallback kullanılır.
const BUILD_HASH = (
  process.env.REACT_APP_BUILD_SHA ||
  process.env.REACT_APP_BUILD_HASH ||
  `v4_${process.env.REACT_APP_VERSION || require('../package.json').version || '0.0.0'}`
);
// Expose globally so myLiveService can include it in queue payloads for stale-client detection
if (typeof window !== 'undefined') window.__MYLASA_BUILD_HASH__ = BUILD_HASH;



function App() {
  // ─── Auth state (Zustand authStore) ─────────────────────────────────────
  const user = useAuthStore(selectUser);
  const currentUserProfile = useAuthStore(selectProfile);
  const loading = useAuthStore(selectLoading);
  const initAuth = useAuthStore(selectInitAuth);
  // Sosyal giriş: profil çözüldü ama doc yok → kullanıcı adı seçtir.
  const profileResolved = useAuthStore((s: any) => s.profileResolved);
  const profileMissing = useAuthStore((s: any) => s.profileMissing);
  // EMİR #94.3 — profil ne okunabildi ne de yokluğu sunucudan doğrulandı.
  const profileUnreachable = useAuthStore((s: any) => s.profileUnreachable);
  const retryProfile = useAuthStore((s: any) => s.retryProfile);
  const profileRetrying = useAuthStore((s: any) => s.profileRetrying);
  // ─── Navigation state (Zustand navigationStore) ─────────────────────────
  const activePage = useNavigationStore(selectActivePage);
  const setActivePage = useNavigationStore(selectSetActivePage);
  // P0 Route Fix: /u/:username URL'inden gelen username — kendi profili değilse Profile'a ownUid=null gönderilir
  const profileRouteUsername = useNavigationStore(selectProfileRouteUsername);
  const setProfileRouteUsername = useNavigationStore(selectSetProfileRouteUsername);
  const modalContent = useNavigationStore(selectModalContent);
  const setModalContent = useNavigationStore(selectSetModalContent);
  const modalData = useNavigationStore(selectModalData);
  const setModalData = useNavigationStore(selectSetModalData);
  // P1-6 — Profile.js ile ortak breakpoint (utils/useIsMobile, 768).
  const isMobile = useIsMobile();


  const pushedByAppRef = useRef(false);

  // Profil modalını, profil modalından açınca geri dönüş için küçük "return stack"
  // Şekil UYDURULMADI: `app/appRouting`in `ModalDonusDurumu` sözleşmesi
  // (`usePopstateHandler` bu ref'i tam olarak o tiple bekliyor).
  const modalReturnRef = useRef<ModalDonusDurumu | null>(null);

  // handleNavChange içinde her zaman güncel değerlere erişmek için ref'ler
  const activePageRef = useRef(activePage);
  const isMobileRef = useRef(isMobile);
  const currentUserProfileRef = useRef(currentUserProfile);
  useEffect(() => { activePageRef.current = activePage; }, [activePage]);
  useEffect(() => { isMobileRef.current = isMobile; }, [isMobile]);
  useEffect(() => { currentUserProfileRef.current = currentUserProfile; }, [currentUserProfile]);

  // Event handler'larda güncel modal state'e erişmek için ref
  const modalStateRef = useRef<{ content: ModalIcerigi; data: ModalVerisi }>({ content: null, data: null });
  useEffect(() => {
    // ✅ Store ile ref artık AYNI tipi konuşuyor (`ModalVerisi`) → köprü yok.
    modalStateRef.current = { content: modalContent, data: modalData };
  }, [modalContent, modalData]);

  // FAZ 4D — Sayfa geçiş animasyonları. History API routing'e DOKUNMAZ;
  // yalnız görsel katman: activePage değişiminde <main>'e tek seferlik
  // animasyon class'ı uygulanır (animationend'de temizlenir → transform
  // kalmaz, scroll/sticky davranışı etkilenmez).
  //   - profile'a ileri geçiş: sağdan slide-in (280ms)
  //   - popstate (geri/ileri): sağa doğru slide-out hissi (240ms)
  //   - diğer geçişler: hafif fade
  //   - clips/mylive tam ekran yüzeyleri animasyon DIŞI (snap-scroll).
  const [pageAnim, setPageAnim] = useState("");
  const popNavRef = useRef(false);
  const prevAnimPageRef = useRef(activePage);
  useEffect(() => {
    const onPop = () => { popNavRef.current = true; };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  useEffect(() => {
    const prev = prevAnimPageRef.current;
    if (prev === activePage) return;
    prevAnimPageRef.current = activePage;
    const fullscreen = activePage === "clips" || activePage === "mylive" ||
      prev === "clips" || prev === "mylive";
    const isPop = popNavRef.current;
    popNavRef.current = false;
    // Tam ekran yüzeye geçişte önceki sınıf TEMİZLENİR (yalnız `return` değil).
    // 2026-09-28 ÖLÇÜLDÜ (mobil, prefers-reduced-motion: reduce, Keşfet → MyClips,
    // 3/3): reduced-motion CSS'i `animation: none` yapınca `animationend` HİÇ
    // gelmiyor → `page-anim--*` <main>'de takılı kalıyor → `will-change: transform`
    // kapsayıcı blok olup sabit-konumlu `.mlv3-feed`i 0 px'e çökertiyordu (akış
    // görünmez). Aynı sebeple reduced-motion'da sınıf hiç TAKILMAZ: animasyon
    // zaten yok, temizleyecek olay da yok.
    if (fullscreen) { setPageAnim(""); return; }
    if (typeof window !== "undefined" && typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches) { setPageAnim(""); return; }
    setPageAnim(
      isPop
        ? "page-anim--pop"
        : activePage === "profile"
          ? "page-anim--push"
          : "page-anim--fade"
    );
  }, [activePage]);

  // SW güncelleme toast state
  const [showUpdateToast, setShowUpdateToast] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);

  // Auth state — authStore üzerinden yönetilir
  useEffect(() => initAuth(), [initAuth]);

  // Profil snapshot — authStore içinde yönetilir

  // SW güncelleme dinleyici
  useEffect(() => {
    const onSWUpdate = (e: Event) => {
      setWaitingWorker((e as CustomEvent).detail);
      setShowUpdateToast(true);
    };
    window.addEventListener("sw-update-available", onSWUpdate);
    return () => window.removeEventListener("sw-update-available", onSWUpdate);
  }, []);

  // PR-64 — cross-device "Puanın: X★": girişten sonra kullanıcının kendi
  // myRatings mirror'ını BİR KEZ bounded oku → PR-63 cache'e seed (rozetler
  // pub/sub ile dolar). Fire-and-forget; hata sessiz degrade.
  useEffect(() => {
    if (!user?.uid) return;
    import("./services/myRatingsMirror")
      .then((m) => m.primeMyRatingsMirror(user.uid))
      .catch(() => {});
  }, [user?.uid]);

  // Faz 1C Görev 7 — Push bildirimi başlat (auth hazır olunca, fire-and-forget).
  // Permission zaten 'denied' ise veya VAPID key yoksa sessizce geçer.
  useEffect(() => {
    if (!user?.uid) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await initPushNotifications({ requestPermission: false });
        if (cancelled) return;
        if (!r?.ok) {
          // eslint-disable-next-line no-console
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[push] init threw:", hataMesaji(e) || e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  // P4-4 — Yeni kullanici onboarding wizard'i. users/{uid}.interests YOK +
  // onboardingCompletedAt YOK ise BIR KEZ acilir. localStorage cache
  // idempotency saglar; hook icinde detayli yorum var.
  const welcomeWizard = useWelcomeWizard(user?.uid || null);

  // P0-5 — KVKK açık rıza kapısı (auth-ready'de bir kez kontrol;
  // localStorage cache + fail-open detayları hook içinde).
  const consentGate = useConsentGate(user?.uid || null);

  // P3-1 — Auth state değişiminde hesabı cihaz listesine kaydet/tazele
  // (profil yüklenince username/avatar dolu gelir; idempotent).
  useEffect(() => {
    if (!user?.uid) return;
    saveAccount(
      user.uid,
      currentUserProfile?.kullaniciAdi || currentUserProfile?.username || "",
      currentUserProfile?.profilFoto || currentUserProfile?.photoURL || "",
    );
  }, [user?.uid, currentUserProfile?.kullaniciAdi, currentUserProfile?.username, currentUserProfile?.profilFoto, currentUserProfile?.photoURL]);

  // Faz 1C Görev 7 — SW'dan gelen bildirim tıklama mesajlarını dinle.
  // Kullanıcı zaten açık bir sekmedeyken bildirime tıklarsa, SW yeni tab
  // açmak yerine mevcut sekmeye postMessage gönderir; biz de deep-link uygularız.
  useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
    const onSwMessage = (event: MessageEvent) => {
      try {
        const d = event?.data || {};
        if (d.type !== "FCM_NOTIFICATION_CLICK") return;
        const link = String(d.link || "");
        // L-29.5: Click telemetry — notif doc'una clicked/clickedAt
        // işaretle. Rules update: alıcı + affectedKeys hasOnly(read,
        // clicked, clickedAt). Fail-soft (fire-and-forget).
        const notifId = d?.data?.notifId || "";
        if (notifId) {
          try {
            updateDoc(doc(db, "notifications", String(notifId)), {
              clicked: true,
              clickedAt: fsServerTimestamp(),
            }).catch(() => {});
          } catch (_) {}
        }
        if (!link) return;
        // L-29: V3 deep link shortcut. `/c/{clipId}` pattern'ı (absolute
        // URL veya path) mevcut sekmede full-reload yerine pushState +
        // `mlv3:anchor-clip` event ile anchor edilir. FeedContainerV3
        // listener zaten var (L-16). Diğer linkler legacy path'e düşer.
        let clipId = null;
        try {
          let path = link;
          if (link.startsWith("http")) {
            const u = new URL(link);
            path = u.pathname + u.search + u.hash;
          }
          const m = String(path).match(/^\/c\/([^/?#]+)/);
          if (m) clipId = decodeURIComponent(m[1]);
        } catch (_) {}
        if (clipId) {
          try {
            window.history.pushState({ mlv3: "clip", clipId }, "", `/c/${encodeURIComponent(clipId)}`);
            window.dispatchEvent(
              new CustomEvent("mlv3:anchor-clip", { detail: { clipId } }),
            );
            return;
          } catch (_) {}
        }
        navigateToNotification({ link });
      } catch {
        // no-op
      }
    };
    navigator.serviceWorker.addEventListener("message", onSwMessage);
    return () => {
      try {
        navigator.serviceWorker.removeEventListener("message", onSwMessage);
      } catch {}
    };
  }, []);

  const acceptUpdateAndReload = () => {
    try {
      waitingWorker?.postMessage({ type: "SKIP_WAITING" });
    } catch {}
    window.location.reload();
  };

  // Presence heartbeat — authStore içinde yönetilir

  // ─── Deep link + popstate handler'ları → src/app/appRouting.js ────────────
  useDeepLinkHandler({
    loading,
    user,
    setActivePage,
    setModalContent,
    setModalData,
    setProfileRouteUsername,
    pushedByAppRef,
    // P0-1 — `/c/{id}` dalı, V3 feed'i zaten ekrandaysa pending yerine olayla
    // anchor'lar (pending yalnız MOUNT'ta tüketilir).
    activePageRef,
  });
  usePopstateHandler({
    modalContent,
    modalReturnRef,
    // #888 — bayat sayfa tespiti (URL `/` ama ekranda `/discoveries` duruyor).
    activePageRef,
    setActivePage,
    setModalContent,
    setModalData,
    setProfileRouteUsername,
    pushedByAppRef,
  });
  // PR-V3e2b2 — discovery overlay back/forward: popstate → mlv3:discovery-sync.
  useDiscoveryPopstateSync();

  // Global event → profil modal aç
  useEffect(() => {
    const handler = (e: Event) => {
      const userId = (e as CustomEvent)?.detail?.userId;
      if (!userId) return;

      // IG PARİTESİ — başka kullanıcının profili de PAYLAŞILABİLİR URL alır.
      // Açılışta /{uid} (bare IG profil) push edilir (resolver uid'i de çözer;
      // KullaniciProfili yüklenince /{kullanıcıadı}'na yükseltir). Geri dönüş için MEVCUT modal
      // durumu saklanır → kapat (X) ve donanım/ tarayıcı GERİ tuşu TEK yoldan
      // (popstate) modalı kapatıp önceki durumu (ör. yorum modalı) restore eder.
      modalReturnRef.current = {
        modalContent: modalStateRef.current.content,
        modalData: modalStateRef.current.data,
        // Altdaki modal kendi permalink history girişini push etmiş olabilir
        // (ör. /p/{id} yorum modalı) → bu bayrağı KORU, restore'da geri ver
        // (yoksa altdaki modalın kapanış mantığı bozulur, bayat /p girişi kalır).
        pushedByApp: pushedByAppRef.current,
      };
      try {
        window.history.pushState(
          { __profileModal: true, uid: String(userId) },
          "",
          `/${encodeURIComponent(String(userId))}`
        );
        pushedByAppRef.current = true;
      } catch {
        pushedByAppRef.current = false;
      }

      setModalData(String(userId));
      setModalContent("viewingProfile");
    };

    window.addEventListener("open-profile-modal", handler);
    return () => window.removeEventListener("open-profile-modal", handler);
  }, [setModalContent, setModalData]);

  // ── P0-1 · GERİ TUŞU: KLİP DEVRİNDEN AKIŞA DÖNÜŞ ────────────────────────
  // `handleViewClip` V3'e devrederken bir history girişi push eder (feed onu
  // `mlv3:clip-shown` ile `/c/{id}`ye replace eder). O girişten GERİ basılınca
  // kullanıcı akışa DÖNMELİ. Bunu `usePopstateHandler` yapamaz ve yapmaması
  // DOĞRUdur: orada `activePage==="clips"` + kök yol için "home yazma" guard'ı
  // ÖLÇÜLEREK konmuş (*"beslemedeki kullanıcıyı home'a atmak gerçek bir
  // regresyondur"*). O guard MyClips SEKMESİ içindir; buradaki devir ise
  // akıştan gelen, geri dönüşü olan bir KATMANdır — ayrımı yalnız devri
  // başlatan taraf bilebilir, o yüzden bayrak burada tutulur.
  //
  // Ayrım YOL ile yapılır, state ile DEĞİL: feed'in `replaceState`i bizim
  // state'imizi zaten eziyor; ayrıca feed içinden profil/yorum açılıp geri
  // basıldığında yol hâlâ `/c/...` olur → o pop devir çıkışı DEĞİLDİR.
  const klipDevriRef = useRef(false);
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onPop = () => {
      if (!klipDevriRef.current) return;
      if (activePageRef.current !== "clips") { klipDevriRef.current = false; return; }
      let yol = "/";
      try { yol = window.location.pathname || "/"; } catch (_) { /* no-op */ }
      // Hâlâ bir klip yolundayız → feed içi bir katman kapandı, devir sürüyor.
      if (/^\/c\//.test(yol)) return;
      klipDevriRef.current = false;
      setActivePage("home");
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [setActivePage]);
  // Kullanıcı sekme değiştirip clips'ten çıktıysa bayrak bayatlamasın.
  useEffect(() => {
    if (activePage !== "clips") klipDevriRef.current = false;
  }, [activePage]);

  // IG PARİTESİ — Reels/klip akışında aktif klip değişince adres çubuğu /c/{id}'ye
  // güncellensin (paylaşırsa/yenilerse doğru klibi açar). KİLİTLİ feed dosyalarına
  // DOKUNULMAZ: yalnız feed'in zaten yaydığı "mlv3:clip-shown" (aktif klip görünüm)
  // event'i DİNLENİR. pushState DEĞİL replaceState → history şişmez, GERİ tuşu
  // klipler arası gezmek yerine akıştan ÇIKAR (IG reels davranışı). Clips'ten
  // çıkınca (modal yokken) bayat /c/ URL'si temizlenir.
  const prevClipsRef = useRef(activePage === "clips");
  useEffect(() => {
    const wasClips = prevClipsRef.current;
    const isClips = activePage === "clips";
    prevClipsRef.current = isClips;

    if (isClips) {
      const onShown = (e: Event) => {
        if (activePageRef.current !== "clips") return;
        const clipId = (e as CustomEvent)?.detail?.clipId;
        if (!clipId) return;
        try {
          const target = `/c/${encodeURIComponent(String(clipId))}`;
          if (window.location.pathname !== target) {
            window.history.replaceState({ mlv3: "clip", clipId }, "", target);
          }
        } catch (_) { /* no-op */ }
      };
      window.addEventListener("mlv3:clip-shown", onShown);
      return () => window.removeEventListener("mlv3:clip-shown", onShown);
    }

    // Clips'ten ÇIKIŞ: bayat /c/ URL'sini sıfırla — ama yalnız hiçbir modal
    // (viewingClip/viewingComments) açık değilse (onların kendi /c,/p URL'si
    // olabilir, ezme).
    if (wasClips && !modalStateRef.current.content) {
      try {
        if (/^\/c\//.test(window.location.pathname)) {
          window.history.replaceState({}, "", "/");
        }
      } catch (_) { /* no-op */ }
    }
    return undefined;
  }, [activePage]);

  // MyClips'te status bar + navigation bar siyah, diğer sayfalarda marka moru — standalone PWA için
  useEffect(() => {
    const isClips = activePage === 'clips';
    const color = isClips ? '#000000' : '#6b5cff';
    const themeTag = document.querySelector('meta[name="theme-color"]');
    if (themeTag) themeTag.setAttribute('content', color);
    const navTag = document.querySelector('meta[name="navigation-bar-color"]');
    if (navTag) navTag.setAttribute('content', color);
    // Reels parite — global body sınıfı: phone sistem nav bar siyah, body
    // arka planı tam siyah, scroll bounce kapalı. CSS target eden modüller
    // (FeedContainerV3.css gibi) bu sınıfa göre özel kural koyabilir.
    try {
      if (isClips) document.body.classList.add('mlv3-clips-fullscreen');
      else document.body.classList.remove('mlv3-clips-fullscreen');
    } catch (_) {}
  }, [activePage]);

  // IG paritesi — hikâye açılınca adres /stories/{kullanıcı}/{id} olsun
  // (profil/mekan modalıyla AYNI mekanizma: önceki durum saklanır, pushState +
  // popstate ile X/GERİ tek yoldan kapat+restore). Hikâyeler kısa ömürlü →
  // soğuk deep-link reconstruct'ı YOK (cold /stories → app açılır); URL yalnız
  // uygulama-içi yansıma + paylaşılabilir adres çubuğu içindir.
  /** Hikâye modalı verisi — gövdenin okuduğu üç alan. */
  const pushStoryUrl = useCallback((data: {
    kullaniciAdi?: string;
    username?: string;
    stories?: Array<{ id?: string }>;
  } | null | undefined) => {
    try {
      const uname = data?.kullaniciAdi || data?.username || "";
      const sid = data?.stories?.[0]?.id || "";
      if (!uname) return;
      modalReturnRef.current = {
        modalContent: modalStateRef.current.content,
        modalData: modalStateRef.current.data,
        pushedByApp: pushedByAppRef.current,
      };
      const target = sid
        ? `/stories/${encodeURIComponent(uname)}/${encodeURIComponent(sid)}`
        : `/stories/${encodeURIComponent(uname)}`;
      window.history.pushState({ __storyModal: true }, "", target);
      pushedByAppRef.current = true;
    } catch {
      pushedByAppRef.current = false;
    }
  }, []);

  // mlv3:open-story — V3 feed avatar tıklamasında hikaye aç
  useEffect(() => {
    const onOpenStory = (e: Event) => {
      const data = (e as CustomEvent)?.detail;
      if (!data || !Array.isArray(data.stories) || data.stories.length === 0) return;
      pushStoryUrl(data);
      setModalData(data);
      setModalContent("viewingStory");
    };
    window.addEventListener("mlv3:open-story", onOpenStory);
    return () => window.removeEventListener("mlv3:open-story", onOpenStory);
  }, [setModalContent, setModalData, pushStoryUrl]);

  // PR-V2d — mlv3:open-place: MyClips feed venue chip tıklamasında mekan
  // detayını aç (/place/{id} push + klip duvarı). handleViewPlaceDetail zaten
  // pushState + modalReturn deseniyle URL/cold-load paritesini sağlıyor.
  // placeName varsa (chip'ten) modal başlığı cold-load fetch'i beklemeden dolu.
  useEffect(() => {
    const onOpenPlace = (e: Event) => {
      const placeId = (e as CustomEvent)?.detail?.placeId;
      if (!placeId || typeof placeId !== "string") return;
      handleViewPlaceDetail({
        placeId,
        name:
          typeof (e as CustomEvent)?.detail?.placeName === "string"
            ? (e as CustomEvent).detail.placeName
            : null,
      });
    };
    window.addEventListener("mlv3:open-place", onOpenPlace);
    return () => window.removeEventListener("mlv3:open-place", onOpenPlace);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNavChange = useCallback((tab: string) => {
    modalReturnRef.current = null;

    // Ref'lerden güncel değerleri al - stale closure sorunu olmaz
    const curActivePage = activePageRef.current;
    const curProfile = currentUserProfileRef.current;

    // IG-tarzı: AKTİF sekmeye tekrar basınca içeriği en üste al (scroll-to-top).
    // Bu tıklama şu an görünür bir iş yapmıyordu; yalnızca EKLEME — mevcut
    // navigasyon/history akışı aşağıda aynen devam eder, hiçbir şey bozulmaz.
    if (tab === curActivePage && (tab === "home" || tab === "explore")) {
      tapHaptic();
      try {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (_) {
        window.scrollTo(0, 0);
      }
      // Kendi scroll konteynerinde olan yüzeyler dinleyip en üste alabilsin.
      try {
        window.dispatchEvent(
          new CustomEvent("app:tab-reselect", { detail: { tab } }),
        );
      } catch (_) {
        /* CustomEvent desteklenmiyorsa sorun değil */
      }
    }

    const path = window.location.pathname;
    const isPermalink = isAnyPermalinkPath(path);
    // DM kendi /direct URL'sini yönetir → permalink'ten DM'e geçişte buradaki
    // async history.back() ile DM'in pushState'i YARIŞMASIN (ajan BUG-A): bu
    // blok messages için ATLANIR; permalink entry'si aşağıda /direct ile
    // replace edilir (tek, tutarlı işlem). PR-18: notifications da aynı
    // nedenle atlanır (entry /notifications ile replace edilir).
    // URL turu: "createMenu" ve "checkin" de AYNI nedenle atlanır — artık
    // kendi URL'lerini (/create, /create/checkin) yazıyorlar ve buradaki
    // async `history.back()` ile yarışırlarsa permalink entry'si yerine
    // rastgele bir giriş kalırdı (BUG-A'nın birebir eşi). Permalink entry'si
    // aşağıda `olusturUrlYaz(..., { degistir: isPermalink })` ile REPLACE edilir.
    if (
      isPermalink &&
      tab !== "messages" &&
      tab !== "notifications" &&
      tab !== "createMenu" &&
      tab !== "checkin"
    ) {
      if (pushedByAppRef.current) window.history.back();
      else window.history.replaceState({}, "", "/");
      pushedByAppRef.current = false;
    }

    // ─── DISCOVERY: discovery tabı modal açar ─── Mevcut hiçbir logic'e dokunmaz
    if (["createMenu", "messages", "notifications", "checkin", "discovery"].includes(tab)) {
      // Modal açılırken aktif sayfadan çıkılsın
      if (curActivePage !== "home") setActivePage("home");
      // DM: IG paritesi — /direct URL'si (sohbet DmInbox içinde /direct/t/{id}'ye
      // yansır). Permalink üstündeyse REPLACE (entry'yi /direct yap → geri tuşunda
      // klip/post yeniden açılmaz), değilse PUSH. modalData temizlenir (bayat seed yok).
      if (tab === "messages") {
        try {
          if (!/^\/direct/.test(window.location.pathname)) {
            if (isPermalink) {
              window.history.replaceState({ dm: true }, "", "/direct");
            } else {
              window.history.pushState({ dm: true }, "", "/direct");
            }
          }
        } catch (_) { /* no-op */ }
        pushedByAppRef.current = false;
        setModalData(null);
      }
      // PR-18 — IG paritesi: bildirimler kendi yolunu alır (/notifications).
      // DM /direct ile aynı desen: permalink üstündeyse REPLACE (geri tuşunda
      // klip/post yeniden açılmaz), değilse PUSH (geri = modalı kapat).
      if (tab === "notifications") {
        try {
          if (!bildirimYolundaMi(window.location.pathname)) {
            if (isPermalink) {
              window.history.replaceState({ ntf: true }, "", "/notifications");
            } else {
              window.history.pushState({ ntf: true }, "", "/notifications");
            }
          }
        } catch (_) { /* no-op */ }
        pushedByAppRef.current = false;
        setModalData(null);
      }
      // Keşif: sekmeden AÇILIŞTA seed temizlenir. `/discoveries/t/{id}`
      // cold-load'u `modalData={matchId}` bırakıyor; temizlenmezse kullanıcı
      // sohbeti kapatıp modalı kapattıktan SONRA sekmeden tekrar girince
      // BAYAT matchId ile sohbet yeniden açılırdı. DM ve bildirimler zaten
      // aynı korumayı taşıyor — üçüncü kopya değil, aynı desenin eşi.
      if (tab === "discovery") setModalData(null);
      // Composer/kamera + check-in: IG /create paritesi. DM ve bildirimlerle
      // AYNI desen — permalink üstündeyse REPLACE (geri tuşu klip/postu yeniden
      // açmasın), değilse PUSH (geri tuşu katmanı kapatsın). modalData
      // temizlenir: `/create/camera` cold-load'unun bıraktığı `{kamera:true}`
      // bayat kalırsa menüden açılan composer da kamerayla açılırdı.
      if (tab === "createMenu" || tab === "checkin") {
        olusturUrlYaz({ modal: tab }, { degistir: isPermalink });
        pushedByAppRef.current = false;
        setModalData(null);
      }
      setModalContent(tab);
      return;
    }

    if (tab === "explore") {
      const target = "/explore";
      if (window.location.pathname !== target) {
        window.history.pushState({}, "", target);
      }
    } else if (tab === "map") {
      // IG: harita kendi bölüm yolunu alır (/map) — eskiden /explore'da takılı kalıyordu.
      const target = "/map";
      if (window.location.pathname !== target) {
        window.history.pushState({}, "", target);
      }
    } else if (tab === "discoveries") {
      // EMİR #76 — İlk Işıklar keşif galerisi kendi yolunu alır (/discoveries).
      const target = "/discoveries";
      if (window.location.pathname !== target) {
        window.history.pushState({}, "", target);
      }
    } else if (tab === "profile" && curProfile?.kullaniciAdi) {
      const target = `/${encodeURIComponent(curProfile.kullaniciAdi)}`;
      if (window.location.pathname !== target)
        window.history.pushState({}, "", target);
    } else if (tab === "home" || tab === "clips") {
      // home → "/"; clips → "/" tabanı (reels feed mount'ta /c/{clipId}'e
      // yükseltir, IG /reels/{id}). /c/{id}'den home'a geçişte de temizlenir
      // (aşağıdaki segment-korumalı else /c/'yi tutuyordu → home'da bayat URL).
      if (window.location.pathname !== "/")
        window.history.replaceState({}, "", "/");
    } else if (
      // Segment-sınırlı: /paul (bare kullanıcı "paul") yanlışlıkla /p permalink
      // sanılmasın diye (\/|$) eklendi. Yalnız gerçek p/c/u/explore rotaları korunur.
      !/^\/(p|c|u|explore|map)(\/|$)/.test(
        window.location.pathname
      )
    ) {
      window.history.replaceState({}, "", "/");
    }

    // EMİR #89.1 — düz sayfa geçişlerinde akışkan View Transition (progressive;
    // destek yoksa/reduced-motion → birebir mevcut davranış). Feed HARİÇ:
    // "clips" (MyClips pager/poster hassas) + "home" (ana feed) VT'ye girmez —
    // kilitli oynatma sınırı. Yalnız güvenli düz sayfalar (keşfet/harita/keşif/profil).
    const applyNav = () => {
      setModalContent(null);
      setModalData(null);
      setActivePage(tab);
    };
    const vtSafe = tab === "explore" || tab === "map" || tab === "discoveries" || tab === "profile";
    if (vtSafe) withViewTransition(applyNav); else applyNav();
  }, [setActivePage, setModalContent, setModalData]); // Zustand setter'lar stabil referans - güvenli dep

  // P5-4 — Taslak devam: DraftsSection (CreatorDashboard) "Devam Et" tıklayınca
  // sessionStorage handoff yazıp bu event'i dispatch eder. Burada clips feed'e
  // geçeriz; MyClipsV3Gate mount'ta bekleyen handoff'u görüp composer'ı açar,
  // composer da getDraft(id) ile state'i restore eder.
  useEffect(() => {
    const onResumeDraft = () => {
      try { handleNavChange("clips"); } catch (_) {}
    };
    window.addEventListener("mlv3:resume-draft", onResumeDraft);
    return () => window.removeEventListener("mlv3:resume-draft", onResumeDraft);
  }, [handleNavChange]);

  // ══════════════════════════════════════════════════════════════════════
  // P0 — "OLUŞTUR" OLAYININ DİNLEYİCİSİ (ölü kolun eksik yarısı)
  // ══════════════════════════════════════════════════════════════════════
  // ÖLÇÜLDÜ (2026-09-03): `mlv3:open-create` olayı depoda İKİ yerden
  // GÖNDERİLİYOR ama HİÇBİR yerde DİNLENMİYORDU:
  //   · `src/UserPosts.tsx:940` — boş profil ızgarasındaki "İlk klibini
  //     yükle" / "İlk gönderini paylaş" düğmeleri (kendi yorumu bu
  //     dinleyicinin VAR olduğunu söylüyordu: *"App.js'te global
  //     `mlv3:open-create` event'i ile dinlenir"* — o cümle YANLIŞTI),
  //   · `src/ProfileMobile.tsx` — profil "+" alt sayfası (aşağıdaki fix).
  // Yani iki ayrı yüzeydeki "oluştur" düğmeleri sessizce hiçbir şey
  // yapmıyordu. `mlv3:open-compose` bu boşluğu dolduramaz: onu YALNIZ
  // `MyClipsV3Gate` dinler ve Gate yalnız clips sekmesinde mount olur.
  //
  // 🧭 Kanon "susturma yollarını say"ın kardeşi: bir olayın gönderilmesi,
  //    dinlendiğinin kanıtı değildir. İki uç ayrı ayrı ölçülmeli.
  //
  // Sözleşme: `detail.tur` bir OLUŞTURMA modalı adıysa (`newclip`/`newpost`/
  // `checkin`) doğrudan o katman açılır; `mylive` sekme değiştirir; hiçbiri
  // değilse (veya detail yoksa) "+" menüsü açılır — yani en kötü hâlde
  // kullanıcı seçim ekranını görür, ASLA sessiz kalmaz.
  useEffect(() => {
    const onOpenCreate = (e: Event) => {
      const tur = (e as CustomEvent<{ tur?: string }>)?.detail?.tur;
      if (tur === "mylive") { setActivePage("mylive"); return; }
      const hedef = olusturModaliMi(tur) ? (tur as OlusturModali) : "createMenu";
      // Katman → push (olusturUrl.ts'in kendi kuralı): geri tuşu katmanı
      // kapatır, altındaki profili değil. Bayat `{kamera:true}` sızmasın.
      olusturUrlYaz({ modal: hedef });
      setModalData(null);
      setModalContent(hedef);
    };
    window.addEventListener("mlv3:open-create", onOpenCreate);
    return () => window.removeEventListener("mlv3:open-create", onOpenCreate);
  }, [setActivePage, setModalContent, setModalData]);

  const handleViewProfile = (userId?: string) => {
    // Kendi profiline tıklanınca (harita avatarı, feed vb.) modal yerine profil sayfasına git
    if (userId === user?.uid) {
      setActivePage("profile");
      return;
    }
    window.dispatchEvent(
      new CustomEvent("open-profile-modal", { detail: { userId } })
    );
  };

  // FAZ 35 (Audit2-D) — Konum sekmesi banner'ında "profil'den şehir girin"
  // tıklanınca profil sekmesine yönlendir. FeedContainerV3 olayı dispatch eder.
  useEffect(() => {
    function onOpenProfileEdit() {
      try { setActivePage("profile"); } catch (_) {}
    }
    window.addEventListener("mlv3:open-profile-edit", onOpenProfileEdit);
    return () => window.removeEventListener("mlv3:open-profile-edit", onOpenProfileEdit);
  }, [setActivePage]);

  const handleViewComments = (post: { id?: string } | null | undefined) => {
    // IG paritesi — gönderi detayı paylaşılabilir URL alır: /p/{id}. Mekan/
    // profil modalıyla AYNI mekanizma: önceki modal durumu saklanır, pushState
    // ile URL eklenir; X/GERİ tek yoldan (popstate) kapatıp restore eder.
    // Cold-load matcher (appRouting mPost → openPostFromUrl → viewingComments)
    // ve kapanış (appModals closeModal + popstate) zaten hazır; eksik olan tek
    // şey açılışta URL push'uydu.
    const postId = post?.id || null;
    if (postId) {
      modalReturnRef.current = {
        modalContent: modalStateRef.current.content,
        modalData: modalStateRef.current.data,
        pushedByApp: pushedByAppRef.current,
      };
      try {
        window.history.pushState(
          { __postModal: true, postId: String(postId) },
          "",
          `/p/${encodeURIComponent(String(postId))}`
        );
        pushedByAppRef.current = true;
      } catch {
        pushedByAppRef.current = false;
      }
    }
    setModalData(post);
    setModalContent("viewingComments");
  };

  const handleViewClip = (clip: { id?: string } | null | undefined) => {
    // ── P0-1 · AKIŞ KLİBİ ARTIK V3 TAM-EKRANDA AÇILIR ──────────────────────
    // ÖLÇÜLEN KUSUR (denetim §3.2): akıştaki klip dokunuşu `viewingClip`
    // legacy modalını açıyordu — mobilde YALNIZ ✕ + video; ★, yorum yazma,
    // paylaş, kaydet ve sağ ray YOKTU. Aynı klip MyClips sekmesinde tam
    // deneyimle açılıyor; kullanıcı tek içeriği iki farklı ürün gibi görüyordu.
    //
    // Devir mekanizması İCAT EDİLMEDİ: profil ızgarası → reels yolunun ta
    // kendisi (`pendingV3Anchor` + `setActivePage("clips")`; feed MOUNT'ta
    // senkron tüketir). Kilitli oynatma/pager/poster dosyalarına DOKUNULMAZ —
    // burada yapılan tek şey YÖNLENDİRMEdir.
    //
    // `scopedClips` BİLEREK verilmiyor (yalnız `scopedUid`): bunu üretecek
    // `clipsRepo.mapPlainClipsToPlayable` ~50 kB'lik veri zincirini ANA
    // PAKETE taşır (#1019'da ölçülüp dinamik import'a alınmıştı). `scopedUid`
    // tek başına DESTEKLENEN giriştir — `FeedContainerV3`in scoped init-load
    // effect'i tam bu hâl için yazılmış ("Yalnız pending path scopedUid'i
    // scopedSource olmadan set eder") ve klibi içeren sayfaya kadar çeker.
    // Karar SAF (`akisKlibiAcmaKarari`), uygulaması AYRI ve birim testli
    // (`klipV3Devri`) — ikisi de `App` ağacı ayağa kalkmadan ölçülebilir.
    const karar = akisKlibiAcmaKarari(clip, activePageRef.current);
    if (karar.hedef === "v3feed") {
      v3KlibineDevret(karar, {
        setActivePage,
        setModalContent,
        setModalData,
        pushedByAppRef,
        devirRef: klipDevriRef,
      });
      return;
    }
    // ── YEDEK YOL: V3 OLMAYAN (v1/v2) klipler ──────────────────────────────
    // Aşağısı DEĞİŞMEDİ. V3 feed'i eski klipleri oynatamaz (`clipsRepo`
    // şekli yok) → onları oraya yollamak siyah ekran üretirdi.
    // IG /reels/{id} paritesi — legacy klip MODAL'ı açılışı da paylaşılabilir URL
    // alır: /c/{clipId}. Post (handleViewComments) / mekan (handleViewPlaceDetail)
    // modalıyla BİREBİR AYNI mekanizma: önceki modal durumu saklanır, pushState ile
    // URL eklenir, pushedByAppRef işaretlenir; X (closeModal permalink dalı) ve
    // donanım/tarayıcı GERİ (popstate viewingClip dalı) tek yoldan kapatır.
    // Cold-load matcher (appRouting mClip → openClipFromUrl → viewingClip) ve
    // kapanış zaten hazırdı; eksik olan tek şey açılışta URL push'uydu (App.tsx'in
    // clips-exit efekti bile viewingClip'in "kendi /c URL'si"ni ezmemek için
    // korur — altyapı bu URL'yi bekliyordu). NOT: bu YALNIZ legacy modal açılışı;
    // V3 aktif-klip FEED yolu ayrıdır (mlv3:clip-shown → replaceState, dokunulmaz).
    // Karar SAF helper'da (computeClipModalPush): geçersiz/eksik clipId → push yok;
    // URL zaten hedefteyse → push yok (duplicate history guard). App yalnız uygular.
    const decision = computeClipModalPush(clip?.id, window.location.pathname);
    if (decision.push) {
      modalReturnRef.current = {
        modalContent: modalStateRef.current.content,
        modalData: modalStateRef.current.data,
        pushedByApp: pushedByAppRef.current,
      };
      try {
        window.history.pushState(decision.state, "", decision.target);
        pushedByAppRef.current = true;
      } catch {
        pushedByAppRef.current = false;
      }
    }
    setModalData(clip);
    setModalContent("viewingClip");
  };

  const handleViewStory = (data: Parameters<typeof pushStoryUrl>[0]) => {
    pushStoryUrl(data);
    setModalData(data);
    setModalContent("viewingStory");
  };

  const handleViewPlaceDetail = (placeData: {
    placeId?: string;
    refId?: string;
    id?: string;
    name?: string | null;
  } | null | undefined) => {
    // IG paritesi — mekan detayı da paylaşılabilir URL alır: /place/{placeId}.
    // Profil modalıyla AYNI mekanizma: önceki modal durumu saklanır, pushState
    // ile URL eklenir; X/GERİ tek yoldan (popstate) kapatıp restore eder.
    const placeId = placeData?.placeId || placeData?.refId || placeData?.id || null;
    if (placeId) {
      modalReturnRef.current = {
        modalContent: modalStateRef.current.content,
        modalData: modalStateRef.current.data,
        pushedByApp: pushedByAppRef.current,
      };
      try {
        window.history.pushState(
          { __placeModal: true, placeId: String(placeId) },
          "",
          `/place/${encodeURIComponent(String(placeId))}`
        );
        pushedByAppRef.current = true;
      } catch {
        pushedByAppRef.current = false;
      }
    }
    setModalData(placeData);
    setModalContent("placeDetail");
  };

  const handleViewUserFromPlace = (userId?: string) => {
    setModalContent(null);
    setModalData(null);
    handleViewProfile(userId);
  };

  const handleStartMessage = (userId?: string) => {
    // DM modalı için tek /direct history girişi (DmInbox sohbeti çözünce
    // /direct/t/{id}'ye replace eder).
    try {
      if (!/^\/direct/.test(window.location.pathname)) {
        window.history.pushState({ dm: true }, "", "/direct");
      }
    } catch (_) { /* no-op */ }
    setModalData(userId);
    setModalContent("messages");
  };

  const handlePlaceSelectForCheckIn = (place: ModalVerisi) => {
    // Mekân seçimi YENİ BİR KATMAN (seçici → detay) → push: geri tuşu seçiciye
    // döner. İsim/adres URL'de taşınamaz (yalnız placeId) ama `history.state`te
    // yaşar → YENİLEMEDE ve geri/ileri'de detay TAM veriyle geri gelir.
    const mekan = (place && typeof place === "object" ? place : {}) as MekanYuku;
    olusturUrlYaz({ modal: "newCheckInDetail", placeId: mekan.id }, { mekan });
    setModalData(place);
    setModalContent("newCheckInDetail");
  };

  const handleOpenFriendPicker = () => {
    setModalContent("friendPicker");
  };

  const handleSaveFriendSelection = async (selectedFriends: string[]) => {
    try {
      if (!user) return;
      const ref = doc(db, "users", user.uid);
      // DENETİM FIX (P0): Eskiden `mapFriends` alanına yazılıyordu ama okuyan
      // taraf (MapDesktop / useFollowingFriends / rules) `sharingWhitelist` +
      // `sharingMode==='selected_friends'` bekliyor → "sadece şu arkadaşlar"
      // özelliği hiç çalışmıyordu. Doğru alan adlarına yaz ve modu aktif et.
      await setDoc(
        ref,
        {
          sharingWhitelist: Array.isArray(selectedFriends) ? selectedFriends : [],
          sharingMode: "selected_friends",
          isSharing: true,
        },
        { merge: true }
      );
      // FIX: Kaydettikten sonra picker'ı kapat — eskiden modal açık kalıyordu,
      // hiçbir geri bildirim yoktu → kullanıcı "seçim kaydedilmiyor" sanıyordu.
      setModalContent(null);
      setModalData(null);
    } catch (e) {
      console.warn("[handleSaveFriendSelection] hata:", hataMesaji(e) || e);
      alert("Arkadaş seçimi kaydedilemedi, tekrar dene.");
    }
  };

  const renderPageContent = () =>
    renderPage({
      activePage,
      user,
      currentUserProfile,
      profileRouteUsername,
      isMobile,
      handlers: {
        handleViewProfile,
        handleViewComments,
        handleViewClip,
        handleViewStory,
        handleViewPlaceDetail,
        handleNavChange,
        setActivePage,
        handleStartMessage,
      },
    });
  const renderModal = () =>
    renderModalContent({
      modalContent,
      // ✅ BORÇ KAPANDI: burada `as ModalVerisi` köprüsü vardı ve yorumu
      // "sözleşme bu noktada YALAN … gerçek çözüm `ModalVerisi`yi
      // dürüstleştirmek, ayrı tur" diyordu. O tur yapıldı — tip artık
      // `string` dalını da taşıyor ve store `unknown`dan çıktı, köprü
      // kendiliğinden gereksiz kaldı (tsc ile doğrulandı, tahminle değil).
      modalData,
      // `as`: `renderModal` BURADA tanımlanıyor ama YALNIZ 892. satırdaki
      // `if (!user) return <Auth />` guard'ından SONRA çağrılıyor. TS kapanışı
      // guard'dan önce kurulduğu için bunu göremiyor. `appModals`ın gövdesi
      // `user.uid`i guard'sız okuyor, dolayısıyla orada `| null` yazmak
      // gövdeyi yalanlardı — daraltma çağrı yerinde, guard satırına atıfla.
      user: user as { uid: string },
      currentUserProfile,
      isMobile,
      refs: { modalReturnRef, pushedByAppRef },
      // #888 — `setProfileRouteUsername` eklendi: «Tekrar dene» artık SPA içinde
      // `resolvePathToPage`i çağırıyor ve o sözleşme bu setter'ı zorunlu kılıyor.
      setters: { setModalContent, setModalData, setActivePage, setProfileRouteUsername },
      handlers: {
        handleViewProfile,
        handleStartMessage,
        handlePlaceSelectForCheckIn,
        handleOpenFriendPicker,
        handleSaveFriendSelection,
        handleViewUserFromPlace,
        // #832 — profil MODALINDAKİ check-in mekân butonunun üreticisi.
        // (Sayfa yolu `renderPage` handlers'ında zaten vardı; modal yolu YOKTU.)
        handleViewPlaceDetail,
      },
      isAnyPermalinkPath,
    });

  // E-posta doğrulama linki (mailden gelen) markalı sayfada işlenir.
  // Oturum/loading durumundan bağımsız — link mail istemcisinin
  // tarayıcısında, oturumsuz da açılabilir.
  if (authActionYoluMu(window.location.pathname)) return <AuthActionHandler />;

  // PR-50 — Kart template permalink (/card/:code): oturumsuz + cold-load açılır
  // (statik cardCatalog; Firestore/auth YOK). auth-duvarından ÖNCE, /auth/action
  // deseniyle aynı. Sahiplik göstermez; yalnız kart template'i.
  {
    // Desen `cozucuOncesiRotalar.ts`te (TEK KAYNAK): ölü rota kapısı da AYNI
    // yüklemi okur, yoksa bu meşru yolun üstüne «Sayfa bulunamadı» basardı.
    const kartKodu = kartSablonKoduCoz(window.location.pathname);
    if (kartKodu) return <Suspense fallback={null}><CardTemplatePage code={kartKodu} /></Suspense>;
    // EMİR #79 — kart paylaşım-cold-redirect'i: renderCardShare insanı /?card={code}'e
    // yollar (path /card/** fonksiyona gider). Oturumsuz kart template'i aynı şekilde açılır.
    if (window.location.pathname === "/" && window.location.search) {
      const qc = new URLSearchParams(window.location.search).get("card");
      if (qc && /^[A-Za-z0-9_-]{1,40}$/.test(qc)) return <Suspense fallback={null}><CardTemplatePage code={qc} /></Suspense>;
    }
  }

  // EMİR #77 — Yasal sayfalar (/gizlilik-politikasi vb.): oturumsuz + cold-load,
  // auth-duvarından ÖNCE (rıza akışının linklediği 404 sayfaları onarır; /card deseni).
  {
    // Slug listesi `cozucuOncesiRotalar.ts`te (TEK KAYNAK) — bkz. yukarıdaki not.
    const yasal = yasalSlugCoz(window.location.pathname);
    if (yasal) return <LegalPage slug={yasal} />;
  }

  if (loading)
    return (
      <div
        className="loading-container"
        aria-busy="true"
        style={{ background: "#0A0A0B" }}
      />
    );
  if (!user) return <Auth />;

  // Sosyal giriş (Google/Apple) YENİ kullanıcısı: Firebase'e giriş yaptı ama
  // henüz users/{uid} profili yok → kullanıcı adı seçtirip profili oluştur.
  // profileResolved guard'ı sayesinde mevcut kullanıcılar (profil var) ve
  // ilk-yükleme loading penceresi ETKİLENMEZ.
  // EMİR #94.3 — YANLIŞ YOKLUK KAPISI. Bu satır `profileUnreachable`
  // kontrolünden SONRA gelmek ZORUNDA: profil okunamadığında (ağ yok) eskiden
  // buraya düşülüyor ve profili TAM olan kullanıcıya "kullanıcı adı seç"
  // deniyordu. Sanal dünya yönlendirilmiş koşusu bunu ölçtü; kontrol kolu aynı
  // anda normal boş-akış ekranını veriyordu.
  // ── ÖLÇÜM ANAHTARI (ÜRETİMDE İNERT) ──────────────────────────────────
  // Bu kapı ŞU AN GLOBAL: profil okunamayınca kullanıcı hangi sayfada olursa
  // olsun (/map, /notifications, /direct, /settings/blocked...) tam-ekran hata
  // görüyor. Kapıyı daraltmak bir ürün kararı ve ÖLÇMEDEN yapılamaz: "hangi
  // ekran profil olmadan gerçekten anlamlı render EDEMİYOR?" sorusunun cevabı
  // ölçülmeden daraltılırsa, iki gün önce kapattığımız yanlış-yokluk hatasının
  // kardeşi geri gelir.
  // Ölçmenin tek yolu kapıyı geçici olarak devre dışı bırakıp her ekranın
  // profilsiz ne yaptığına bakmak. Anahtar EYE_MODE'a bağlı — EYE_MODE yalnız
  // localhost/127.0.0.1 + `?eye=1` ile açılır (src/firebase.ts:39-42), prod
  // host'ta `?eye=1` verilse bile false kalır → CANLI DAVRANIŞ DEĞİŞMEZ.
  // Üstüne ayrıca açık bir localStorage işareti gerekir; ikisi birden olmadan
  // kapı aynen çalışır.
  // Yüklem AYRI ve SAF (src/app/kapiOlcumBypass.ts) — satır-içi bırakılsaydı
  // "üretimde ölü" cümlesi test edilemez bir İDDİA olarak kalırdı.
  const kapiOlcumBypass = kapiOlcumBypassAcikMi(
    EYE_MODE,
    typeof window !== "undefined" ? window.localStorage : null,
  );
  // ── KAPI DARALTILDI (ölçüme dayalı) ──────────────────────────────────
  // Kapı eskiden GLOBAL'di: profil okunamayınca kullanıcı NEREDE olursa olsun
  // tam-ekran hata görüyordu. Artık `profilKapisi.ts` karar veriyor; beyan
  // edilmemiş her sayfa KAPALI kalır (varsayılan kapalı). Serbest bırakılan
  // sayfalarda kapı yerine ŞERİT çıkar (aşağıda, <main> içinde) — çünkü
  // "ekran bir şey render etti" ≠ "doğru şeyi render etti".
  const kapiGerekli = profilKapisiGerekliMi(activePage);
  if (profileUnreachable && kapiGerekli && !kapiOlcumBypass) return <ProfilUlasilamiyor onRetry={retryProfile} retrying={profileRetrying} />;
  if (profileResolved && profileMissing) return <Auth socialCompletion />;

  // E-POSTA DOĞRULAMA KAPISI KALDIRILDI (2026-06-30, sahip kararı) —
  // Instagram-parite araştırması: Instagram kayıtta e-postayı bloke etmez,
  // hesap anında aktif olur; doğrulama yalnız güvenlik olaylarında ve
  // kullanıcının kendi başlatmasıyla (Hesap Merkezi) devreye girer. Eski
  // PR-0e "doğrulanmadan giremezsin" kapısı App Check/reCAPTCHA'ya bağımlı
  // mail gönderimi başarısız olduğunda kullanıcıyı KALICI kilitliyordu —
  // gerçek bir kayıt sorununa yol açtı. firestore.rules:isVerified() de
  // aynı politikayla isSignedIn()'e indirildi (tek-nokta, rollback kolay).
  // Self-servis doğrulama: eski EmailVerifyGate.tsx mantığı (App Check
  // bekleme + kademeli cooldown) ProfilDuzenle.tsx EmailDogrulaPanel'e
  // taşındı — opsiyonel "Doğrula" eylemi (Profili Düzenle → E-posta
  // Doğrulama). EmailVerifyGate.tsx dosyası silindi (kullanan kalmadı).

  // Clips tab aktifken tam ekran mod — header + nav gizle (Instagram Reels gibi)
  const isClipsFullscreen = isMobile && activePage === "clips";

  const mainContentClass =
    activePage === "mylive" || isClipsFullscreen
      ? "main-content-full"
      : activePage === "explore" || activePage === "clips" || activePage === "map"
      ? "main-content-wide"
      : "main-content-narrow";

  const renderUpdateToast = () => {
    if (!showUpdateToast) return null;
    const wrap: CSSProperties = {
      position: "fixed",
      left: 12,
      right: 12,
      bottom: 12,
      zIndex: 5000,
      display: "flex",
      justifyContent: "center",
      pointerEvents: "none",
    };
    const card: CSSProperties = {
      pointerEvents: "auto",
      maxWidth: 560,
      width: "100%",
      background: "#111",
      color: "#fff",
      padding: "12px 14px",
      borderRadius: 12,
      boxShadow: "0 8px 24px rgba(0,0,0,.35)",
      display: "flex",
      alignItems: "center",
      gap: 10,
    };
    const btn = {
      marginLeft: "auto",
      background: "#6b5cff",
      color: "#fff",
      border: "none",
      borderRadius: 10,
      padding: "10px 14px",
      fontWeight: 700,
      cursor: "pointer",
    };
    return (
      <div style={wrap}>
        <div style={card}>
          <span role="img" aria-label="update">
            ⬆️
          </span>
          <div style={{ fontSize: 14 }}>
            Yeni bir sürüm hazır. Uygulamayı güncellemek için "Yenile"ye dokun.
          </div>
          <button className="pressable" style={btn} onClick={acceptUpdateAndReload}>
            Yenile
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={`app-container ${!isMobile ? "desktop-layout" : ""} ${activePage === "mylive" ? "mylive-active" : ""}`}>
      {/* PR-1 — emergency modunda kullanıcıya nazik bilgi çubuğu */}
      <CostModeBanner />
      {isMobile && activePage !== "mylive" && !isClipsFullscreen && (
        <LogoBar
          onNotificationClick={() => handleNavChange("notifications")}
          onLiveClick={() => handleNavChange("mylive")}
          onLocationClick={() => handleNavChange("map")}
        />
      )}
      {activePage !== "mylive" && !isClipsFullscreen && (
        isMobile ? (
          <BottomNav
            activeTab={activePage}
            onTabChange={handleNavChange}
            profilePic={currentUserProfile?.profilFoto}
          />
        ) : (
          <SideNav
            activeTab={activePage}
            onTabChange={handleNavChange}
            profilePic={currentUserProfile?.profilFoto}
          />
        )
      )}
      <main
        className={`${mainContentClass} ${pageAnim}`.trim()}
        /* FAZ 4D — animasyon bitince class düşer (transform temizlenir,
           scroll pozisyonu transform-temelli animasyonda zaten korunur). */
        onAnimationEnd={(e) => {
          if (e.target === e.currentTarget && pageAnim) setPageAnim("");
        }}
      >
        {/* KAPI DARALTMA — serbest bırakılan sayfada profil okunamıyorsa
            kullanıcı BUNU BİLEREK baksın. Kapı devralan sayfalarda bu şerit
            hiç render edilmez (zaten tam-ekran hata görünür). */}
        {kesintiSeridiGerekliMi(profileUnreachable, activePage) && (
          <ProfilKesintiSeridi onRetry={retryProfile} retrying={profileRetrying} />
        )}
        {activePage === "home" && (
          /* Hikayeler şeridi çökerse feed yaşamaya devam etsin (12 Haz TDZ
             kesintisinin dersi) — sessiz boundary, key reset gerekmez */
          <AppErrorBoundary variant="silent" label="hikayeler-tray">
            <Hikayeler currentUserProfile={currentUserProfile} />
          </AppErrorBoundary>
        )}
        {/* Sekme içeriği çökerse nav + diğer sekmeler ayakta kalır;
            key=activePage → sekme değişince boundary resetlenir */}
        <AppErrorBoundary variant="inline" label="page" key={`pg-${activePage}`}>
          <Suspense
            fallback={
              // IG-paritesi KOYU SHELL: sayfa chunk'ı yüklenirken BEYAZ
              // "Yükleniyor..." yerine koyu tam-ekran (flash yok). Tüm sayfalar
              // için aynı (clips/mylive zaten böyleydi). Mantık değişmez; yalnız
              // ilk-ziyaret chunk indirme anında görünür.
              <div
                aria-busy="true"
                style={{ position: "fixed", inset: 0, background: "#0A0A0B" }}
              />
            }
          >
            {renderPageContent()}
          </Suspense>
        </AppErrorBoundary>
      </main>
      {/* Modal çökerse app kalır; key=modalContent → yeni modal açılınca reset */}
      <AppErrorBoundary variant="silent" label="modal" key={`md-${modalContent || "none"}`}>
        <Suspense fallback={null}>
          {renderModal()}
        </Suspense>
      </AppErrorBoundary>
      {renderUpdateToast()}
      {/* FAZ 6 GELİŞTİRME 2 — KVKK cookie consent banner. Mount edildikten
          sonra kullanıcı karar verene kadar görünür; karar verildikten
          sonra null döner (component-internal state, App.js dışına
          yansımaz). */}
      {/* Tek-rıza-yüzeyi: KVKK modalı açıkken band render edilmez */}
      <CookieConsentBanner hidden={consentGate.open} />
      {/* P4-4 — Yeni kullanici onboarding wizard. Yalniz interests YOK +
          marker YOK ise acilir; close (skip veya complete) sonrasi cache. */}
      {/* `onGeriCik` = İLK ADIMDA GERİ: sihirbazdan çık ama "done" YAZMA
          (useWelcomeWizard.gizle). `onClose` kalıcıdır, `gizle` değildir. */}
      <WelcomeWizard
        open={welcomeWizard.open && !consentGate.open}
        onClose={welcomeWizard.close}
        onGeriCik={welcomeWizard.gizle}
      />
      {/* P0-5 — KVKK açık rıza modalı. Rıza kaydı olmayan kullanıcıda
          onay verilene kadar açık kalır (dismiss yok); recordConsentV3
          callable'ı serverTimestamp + IP-hash ile immutable kayıt yazar. */}
      <ConsentSheet
        open={consentGate.open}
        busy={consentGate.busy}
        error={consentGate.error}
        onAccept={consentGate.accept}
      />
      {/* EMİR #78 — değer-sonrası push soft-prompt (ilk 5★ / ilk paylaşım). */}
      <FirstSparkPushPrompt />
    </div>
  );
}

export default App;
